import { UseFormReturn } from 'react-hook-form';
import { useLanguage } from '@/hooks/useLanguage';
import { Card, CardContent } from '@/components/ui/card';
import { FormField, FormItem, FormLabel, FormControl, FormMessage } from '@/components/ui/form';
import { cn } from '@/lib/utils';
import { LucideIcon } from 'lucide-react';

interface Service {
  id: string;
  icon: LucideIcon;
}

interface ServiceSelectionStepProps {
  form: UseFormReturn<any>;
  services: Service[];
}

const ServiceSelectionStep = ({ form, services }: ServiceSelectionStepProps) => {
  const { t } = useLanguage();

  const getServiceTranslationKey = (serviceId: string): any => {
    const keyMap: Record<string, string> = {
      'Immigration': 'booking.immigration',
      'Criminal Defense': 'booking.criminalDefense',
      'Personal Injury': 'booking.personalInjury',
      'Family Law': 'booking.familyLaw',
      'Tax': 'booking.tax',
      'Probate & Estate Planning': 'booking.probateEstate',
      'Real Estate': 'booking.realEstate',
      'Business Law': 'booking.businessLaw',
    };
    return t(keyMap[serviceId] as any) || serviceId;
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h3 className="text-2xl font-bold mb-2">{t('booking.selectService' as any)}</h3>
        <p className="text-muted-foreground">{t('booking.selectServiceDescription' as any)}</p>
      </div>

      <FormField
        control={form.control}
        name="service"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="sr-only">{t('booking.selectService' as any)}</FormLabel>
            <FormControl>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {services.map((service) => {
                  const Icon = service.icon;
                  return (
                    <Card
                      key={service.id}
                      className={cn(
                        'cursor-pointer transition-all hover:border-primary',
                        field.value === service.id && 'border-primary bg-primary/5'
                      )}
                      onClick={() => field.onChange(service.id)}
                    >
                      <CardContent className="flex items-center gap-4 p-6">
                        <Icon className="h-8 w-8 text-primary" />
                        <span className="font-semibold text-lg">
                          {getServiceTranslationKey(service.id)}
                        </span>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
};

export default ServiceSelectionStep;
